import type { Directive } from "vue";

// Definición de tipos para las propiedades extendidas del elemento
declare module "vue" {
  interface TooltipHTMLElement extends HTMLElement {
    _tooltip?: HTMLElement;
    _showTooltip?: () => void;
    _hideTooltip?: () => void;
    _updateTooltipPosition?: () => void;
    _handleMouseEnter?: () => void;
    _handleMouseLeave?: () => void;
    _tooltipStyle?: HTMLStyleElement;
    _tooltipTheme?: string;
  }
}

const tooltipDirective: Directive<
  HTMLElement & {
    _tooltip?: HTMLElement;
    _showTooltip?: () => void;
    _hideTooltip?: () => void;
    _updateTooltipPosition?: () => void;
    _handleMouseEnter?: () => void;
    _handleMouseLeave?: () => void;
    _tooltipStyle?: HTMLStyleElement;
    _tooltipTheme?: string;
  },
  string | { text: string; theme?: string }
> = {
  mounted(el, binding) {
     if (!binding.value) return  
    // Obtener texto y tema del tooltip
    const tooltipText =
      typeof binding.value === "string" ? binding.value : binding.value.text;
    const tooltipTheme =
      typeof binding.value === "string" ? null : binding.value.theme;

    // Crear el elemento del tooltip
    const tooltip = document.createElement("div");
    tooltip.className = "vue-tooltip";
    if (tooltipTheme) {
      tooltip.classList.add(tooltipTheme);
      el._tooltipTheme = tooltipTheme;
    }
    tooltip.textContent = tooltipText;

    // Añadir estilos globales solo una vez
    if (!document.getElementById("vue-tooltip-styles")) {
      const style = document.createElement("style");
      style.id = "vue-tooltip-styles";
      style.textContent = `
  .vue-tooltip {
    position: absolute;
    background: linear-gradient(145deg, rgba(30, 30, 46, 0.85), rgba(35, 35, 57, 0.85)) !important;
    color: rgba(240, 245, 255, 0.92);
    padding: 14px 18px;
    border-radius: 12px;
    font-size: 0.9rem;
    font-family: 'Geist', 'Inter', -apple-system, sans-serif;
    font-weight: 450;
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.35),
      0 0 8px rgba(100, 210, 255, 0.25);
    white-space: normal;
    z-index: 9999;
    visibility: hidden;
    opacity: 0;
    transition: all 0.35s cubic-bezier(0.25, 1, 0.5, 1);
    pointer-events: none;
    max-width: 300px;
    text-align: center;
    line-height: 1.6;
    border: 1px solid rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(14px) saturate(160%);
    transform-origin: center bottom;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    overflow: visible;
  }

  /* Flecha inferior transparente */
  .vue-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -8px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid rgba(35, 35, 57, 0.85);
    filter: 
      drop-shadow(0 3px 5px rgba(0, 0, 0, 0.25))
      brightness(1.15);
    z-index: -1;
  }

  /* Animación con transparencia constante */
  @keyframes tooltip-enter-transparent {
    0% {
      opacity: 0;
      transform: translateY(15px) scale(0.95);
    }
    50% {
      transform: translateY(-5px) scale(1.03);
    }
    100% {
      opacity: 0.92; /* Mantiene transparencia */
      transform: translateY(0) scale(1);
    }
  }

  .vue-tooltip.visible {
    visibility: visible;
    opacity: 0.92; /* Transparencia persistente */
    animation: tooltip-enter-transparent 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  /* Variantes con transparencia constante */
  .vue-tooltip.neon {
    background: linear-gradient(145deg, rgba(26, 42, 58, 0.85), rgba(30, 30, 62, 0.85)) !important;
  }
  .vue-tooltip.neon.visible {
    opacity: 0.92;
  }

  .vue-tooltip.carbon {
    background: linear-gradient(145deg, rgba(26, 26, 26, 0.88), rgba(37, 37, 37, 0.88)) !important;
  }
  .vue-tooltip.carbon.visible {
    opacity: 0.92;
  }

  /* Efecto de borde luminoso transparente */
  .vue-tooltip::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(
      135deg,
      rgba(100, 210, 255, 0.25),
      rgba(150, 120, 255, 0.12),
      rgba(100, 210, 255, 0.25)
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
                  linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;
      document.head.appendChild(style);
    }

    document.body.appendChild(tooltip);

    // Función para actualizar la posición del tooltip
    const updateTooltipPosition = (): void => {
      const elRect = el.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      // Posición preferida: arriba centrado
      let top = window.scrollY + elRect.top - tooltipRect.height - 8;
      let left =
        window.scrollX + elRect.left + elRect.width / 2 - tooltipRect.width / 2;

      // Si no hay espacio arriba, mostrar abajo
      if (top < window.scrollY) {
        top = window.scrollY + elRect.bottom + 8;
        tooltip.classList.add("has-arrow-top");
      } else {
        tooltip.classList.add("has-arrow-bottom");
      }

      // Ajustar para no salir del viewport horizontalmente
      left = Math.max(
        5,
        Math.min(left, window.innerWidth - tooltipRect.width - 5)
      );

      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
    };

    // Funciones para mostrar/ocultar con transición
    const showTooltip = (): void => {
      updateTooltipPosition();
      tooltip.classList.add("visible");

      // Actualización adicional en el siguiente frame para contenido dinámico
      requestAnimationFrame(() => {
        updateTooltipPosition();
      });
    };

    const hideTooltip = (): void => {
      tooltip.classList.remove("visible");
      tooltip.classList.remove("has-arrow-top", "has-arrow-bottom");
    };

    // Manejo de eventos con debounce
    let showTimeout: ReturnType<typeof setTimeout>;
    let hideTimeout: ReturnType<typeof setTimeout>;

    const handleMouseEnter = (): void => {
      clearTimeout(hideTimeout);
      showTimeout = setTimeout(showTooltip, 100);
    };

    const handleMouseLeave = (): void => {
      clearTimeout(showTimeout);
      hideTimeout = setTimeout(hideTooltip, 100);
    };

    // Añadir event listeners
    el.addEventListener("mouseenter", handleMouseEnter);
    el.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("scroll", updateTooltipPosition, { passive: true });
    window.addEventListener("resize", updateTooltipPosition, { passive: true });

    // Guardar referencias para limpieza
    el._tooltip = tooltip;
    el._showTooltip = showTooltip;
    el._hideTooltip = hideTooltip;
    el._updateTooltipPosition = updateTooltipPosition;
    el._handleMouseEnter = handleMouseEnter;
    el._handleMouseLeave = handleMouseLeave;
  },

  updated(el, binding) {
    if (!binding.value) return
    // Obtener texto y tema del tooltip
    const tooltipText =
      typeof binding.value === "string" ? binding.value : binding.value.text;
    const tooltipTheme =
      typeof binding.value === "string" ? null : binding.value.theme;

    if (el._tooltip) {
      // Actualizar contenido si cambió
      if (el._tooltip.textContent !== tooltipText) {
        el._tooltip.textContent = tooltipText;
      }

      // Actualizar tema si cambió
      if (tooltipTheme !== el._tooltipTheme) {
        if (el._tooltipTheme) {
          el._tooltip.classList.remove(el._tooltipTheme);
        }
        if (tooltipTheme) {
          el._tooltip.classList.add(tooltipTheme);
        }
        el._tooltipTheme = tooltipTheme || undefined;
      }

      // Si está visible, actualizar posición
      if (el._tooltip.classList.contains("visible")) {
        el._updateTooltipPosition?.();
      }
    }
  },

  unmounted(el) {
    // Limpiar tooltip
    if (el._tooltip) {
      document.body.removeChild(el._tooltip);
      delete el._tooltip;
    }

    // Eliminar event listeners
    if (el._handleMouseEnter) {
      el.removeEventListener("mouseenter", el._handleMouseEnter);
      delete el._handleMouseEnter;
    }

    if (el._handleMouseLeave) {
      el.removeEventListener("mouseleave", el._handleMouseLeave);
      delete el._handleMouseLeave;
    }

    if (el._updateTooltipPosition) {
      window.removeEventListener("scroll", el._updateTooltipPosition);
      window.removeEventListener("resize", el._updateTooltipPosition);
      delete el._updateTooltipPosition;
    }

    // Eliminar estilos si no hay más tooltips
    const tooltips = document.querySelectorAll(".vue-tooltip");
    if (tooltips.length === 0) {
      const styles = document.getElementById("vue-tooltip-styles");
      if (styles) {
        document.head.removeChild(styles);
      }
    }
  },
};

export default tooltipDirective;